/**
* /branches/$branch — หน้ารายสำนักงาน
*
* GOOGLE COMPLIANCE:
* - สร้างเฉพาะ "สำนักงานจริง" ที่มีที่อยู่จดทะเบียนและตรวจสอบได้เท่านั้น
* (OFFICE_BRANCHES) จุดนัดพบนอกสถานที่ทั้งหมดจะ notFound() เสมอ
* เพื่อไม่ให้เกิด doorway pages ตามนโยบาย Google Search Spam
* - เนื้อหาแต่ละหน้าอ้างอิงข้อมูลจริงเฉพาะสำนักงานนั้น (ที่อยู่ / แผนที่ /
* การเดินทาง / บริการที่เปิดให้บริการที่สาขา)
*/
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { provinceNote } from "@/data/_near/province-notes";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SITE, absoluteUrl } from "@/config/sites";
import { buildHreflangLinks } from "@/lib/hreflang";
import { PageHero } from "@/components/PageHero";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { RelatedClusters } from "@/components/RelatedClusters";
import {
  OFFICE_BRANCHES,
  BRANCH_TYPE_LABEL,
  SERVICE_FOCUS_LABEL,
  SERVICE_FOCUS_PATH,
  locationLine,
  type Branch,
} from "@/data/branches/index";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  organizationSchema,
  faqPageSchema,
  localBusinessSchema,
} from "@/lib/schema";

function officeFaqs(name: string, region: string) {
  return [
    {
      question: `${name} เปิดทำการวันไหนบ้าง?`,
      answer:
        "เปิดให้บริการทุกวัน จันทร์–อาทิตย์ เวลา 09:00–18:00 น. แนะนำให้นัดหมายล่วงหน้าทางโทรศัพท์หรือ LINE เพื่อให้ทนายผู้รับรองอยู่ประจำสำนักงานในช่วงเวลาที่คุณสะดวก",
    },
    {
      question: `ต้องนัดหมายก่อนเข้ามาที่${name}หรือไม่?`,
      answer:
        "เข้ามาโดยไม่นัดหมายได้ แต่การนัดหมายล่วงหน้าช่วยให้เราจัดคิวทนายและเตรียมแบบฟอร์มไว้ก่อน และแจ้งเวลาโดยประมาณตามจำนวนและประเภทเอกสารของคุณ",
    },
    {
      question: `${name} รับรองเอกสารเพื่อยื่นต่างประเทศได้ครบขั้นตอนไหม?`,
      answer: `ได้ครบทั้งสายงาน ตั้งแต่ Notarial Services Attorney ที่${region} ต่อด้วยกรมการกงสุล (MFA) และสถานทูตปลายทาง โดยเราตรวจข้อกำหนดของแต่ละหน่วยงานก่อนและประสานเดินเรื่องให้ หลายกรณีจึงไม่ต้องเดินทางเข้ากรุงเทพฯ เอง`,
    },
    {
      question: `ถ้าอยู่ต่างอำเภอหรือต่างจังหวัดใกล้เคียง${region} ต้องเดินทางมาเองไหม?`,
      answer:
        "ไม่จำเป็นครับ เรามีบริการนัดหมายนอกสถานที่ในพื้นที่ใกล้เคียง และรับ–ส่งเอกสารทางไปรษณีย์ EMS ทั่วประเทศ พร้อมแจ้งเลขพัสดุทุกครั้ง",
    },
  ];
}

export const Route = createFileRoute("/branches_/$branch")({
  loader: (__ctx) => { const __d = (({ params }) => {
    const office = OFFICE_BRANCHES.find((b) => b.id === params.branch);
    if (!office) throw notFound();
    return { office: office as Branch };
  })(__ctx); return { ...__d, h: (({ loaderData, params }) => {
    if (!loaderData) {
      return { meta: [{ title: ctrTitle("ไม่พบสำนักงาน") }, { name: "robots", content: "noindex" }] };
    }
    const b = loaderData.office;
    const url = absoluteUrl(`/branches/${params.branch}`);
    const title = `${b.name} — Notary Public & แปลรับรอง ${b.region}`;
    const desc = `${b.name} ${b.street} ${b.locality} ${b.region} เปิดทุกวัน 09:00–18:00 ทนายผู้ทำคำรับรองลายมือชื่อและเอกสาร พร้อมรับรองกงสุลและสถานทูตกว่า 60 ประเทศ นัดหมายได้ทันที`;
    return {
      meta: [
        { title: ctrTitle(title) },
        { name: "description", content: ctrDesc(desc) },
        { property: "og:title", content: ctrTitle(title) },
        { property: "og:description", content: ctrDesc(desc) },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { property: "og:image", content: absoluteUrl(SITE.logoPath) },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: absoluteUrl(SITE.logoPath) },
      ],
      links: [{ rel: "canonical", href: url }, ...buildHreflangLinks(`/branches/${params.branch}`, null, "th")],
      scripts: [
        jsonLdScript(
          buildJsonLd([
            organizationSchema(),
            webPageSchema({ url, title, description: desc, speakable: true }),
            breadcrumbListSchema([
              { name: "หน้าแรก", url: absoluteUrl("/") },
              { name: "จุดบริการ", url: absoluteUrl("/branches") },
              { name: b.name, url },
            ]),
            localBusinessSchema({
              id: b.id,
              name: b.name,
              street: b.street,
              locality: b.locality,
              region: b.region,
              postalCode: b.postalCode,
              country: b.country,
              phone: b.phone,
              lat: b.geo?.lat ?? 0,
              lng: b.geo?.lng ?? 0,
              mapUrl: b.mapUrl,
            }),
            faqPageSchema(officeFaqs(b.name, b.region)),
          ]),
        ),
      ],
    };
  })({ ...__ctx, loaderData: __d }) }; },
  head: ({ loaderData }) => loaderData?.h ?? {},
  component: BranchOfficePage,
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-4 py-16 text-center">
      <h1 className="text-2xl font-semibold">ไม่พบสำนักงานที่ระบุ</h1>
      <p className="mt-2 text-muted-foreground">
        เรามีสำนักงานที่มีที่อยู่จดทะเบียนจริงเพียงไม่กี่แห่ง จุดอื่นเป็นจุดนัดพบนอกสถานที่
      </p>
      <Link to="/branches" className="mt-4 inline-block underline">
        ดูเครือข่ายจุดบริการทั้งหมด →
      </Link>
    </div>
  ),
});

function BranchOfficePage() {
  const { office: b } = Route.useLoaderData() as { office: Branch };
  const faqs = officeFaqs(b.name, b.region);

  return (
    <>
      <PageHero
        eyebrow={BRANCH_TYPE_LABEL[b.branchType]}
        title={`${b.name} — Notary Public และแปลรับรองใน${b.region}`}
        subtitle={b.note || locationLine(b)}
        badges={["สำนักงานจริง", "เปิดทุกวัน 09:00–18:00", "รับรองกงสุล 60+ ประเทศ", "EMS ทั่วประเทศ"]}
      />

      <Breadcrumbs jsonLd
        items={[
          { label: "หน้าแรก", to: "/" },
          { label: "จุดบริการ", to: "/branches" },
          { label: b.shortName || b.name },
        ]}
      />

      <section className="mx-auto max-w-4xl px-4 py-12 cv-auto">
        <div className="rounded-lg border border-border/60 bg-muted/30 p-5">
          <p className="text-sm answer-summary">
            <strong>สรุปคำตอบ:</strong> {b.name} ตั้งอยู่ที่ {locationLine(b)} เปิดให้บริการทุกวัน
            09:00–18:00 น. โทร {b.phone} ให้บริการรับรองลายมือชื่อและเอกสารโดยทนายผู้ทำคำรับรอง
            (Notarial Services Attorney) พร้อมงานแปลรับรอง และเดินเรื่องกรมการกงสุล–สถานทูตต่อเนื่องให้ครบขั้นตอน
          </p>
        </div>

        <h2 className="mt-10 text-2xl font-semibold">ที่อยู่และการติดต่อ</h2>
        <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-[9rem_1fr]">
          <dt className="text-muted-foreground">ที่อยู่</dt>
          <dd>{locationLine(b)}</dd>
          <dt className="text-muted-foreground">โทรศัพท์</dt>
          <dd>
            <a href={`tel:${b.phone.replace(/[^+\d]/g, "")}`} className="underline">
              {b.phone}
            </a>
          </dd>
          <dt className="text-muted-foreground">เวลาทำการ</dt>
          <dd>{b.hours}</dd>
          {b.landmarks.length > 0 && (
            <>
              <dt className="text-muted-foreground">จุดสังเกตใกล้เคียง</dt>
              <dd>{b.landmarks.join(" · ")}</dd>
            </>
          )}
          {b.transit.length > 0 && (
            <>
              <dt className="text-muted-foreground">การเดินทาง</dt>
              <dd>{b.transit.join(" · ")}</dd>
            </>
          )}
        </dl>

        <div className="mt-5 flex flex-wrap gap-4 text-sm">
          <Link to="/contact" className="underline">
            นัดหมายล่วงหน้า →
          </Link>
          {b.mapUrl && (
            <a href={b.mapUrl} target="_blank" rel="noopener noreferrer" className="underline">
              เปิดใน Google Maps →
            </a>
          )}
          {b.provinceSlug && (
            <Link to="/coverage/$province" params={{ province: b.provinceSlug }} className="underline">
              ดูพื้นที่ให้บริการใน{b.region} →
            </Link>
          )}
        </div>

        {b.provinceSlug && (b.provinceSlug !== "bangkok" || b.branchType === "hq") && provinceNote(b.provinceSlug.replace(/-/g, "")) && (
          <>
            <h2 className="mt-12 text-2xl font-semibold">ข้อมูลเฉพาะพื้นที่{b.region}</h2>
            <ul className="mt-3 list-disc space-y-2 pl-5 text-sm">
              {provinceNote(b.provinceSlug.replace(/-/g, ""))!.th.map((t) => (
                <li key={t}>{t}</li>
              ))}
            </ul>
          </>
        )}

        <h2 className="mt-12 text-2xl font-semibold">บริการที่เปิดให้บริการที่สาขานี้</h2>
        <ul className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
          {b.serviceFocus.map((f) => (
            <li key={f}>
              <Link to={SERVICE_FOCUS_PATH[f]} className="underline">
                {SERVICE_FOCUS_LABEL[f]} →
              </Link>
            </li>
          ))}
        </ul>

        <h2 className="mt-12 text-2xl font-semibold">คำถามที่พบบ่อยเกี่ยวกับ{b.shortName || b.name}</h2>
        <div className="mt-3 space-y-4">
          {faqs.map((f) => (
            <div key={f.question} className="rounded-lg border border-border/60 bg-card p-4">
              <h3 className="font-semibold">{f.question}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{f.answer}</p>
            </div>
          ))}
        </div>

        <p className="mt-10 text-sm">
          <Link to="/branches" className="underline">
            ← กลับหน้าเครือข่ายจุดบริการทั้งหมด
          </Link>
        </p>
      </section>

      <RelatedClusters theme="notary" />
    </>
  );
}
